'use client';

import type { ComponentPropsWithoutRef, MouseEvent } from 'react';

import Link from 'next/link';

import { useBack } from '../hooks/useBack';

type Props = Omit<ComponentPropsWithoutRef<typeof Link>, 'href'> & {
  /**
   * The fallback destination — also the Link's `href`, so a cold load / shared
   * link / modifier-click still reaches a working page without JS.
   */
  href: string;
};

/**
 * The default back control: a `next/link` whose click pops history when possible
 * and otherwise navigates to `href`. Accepts all `Link` props (className,
 * children, `aria-label`, …), so callers just drop in their icon + styles. For
 * custom markup (buttons, menu items) use `BackHandler`.
 */
export function BackLink({ href, onClick, ...rest }: Props) {
  // Pass `href` as the fallback so a click with no in-app history to pop
  // REPLACES (climb to the parent) rather than pushing — a push would leave this
  // page in the stack and let the parent's own Back bounce straight back to it.
  // The Link's `href` still drives cold loads / no-JS / modifier-clicks (new tab).
  const { onClick: backOnClick } = useBack(href);

  return (
    <Link
      href={href}
      onClick={(event: MouseEvent<HTMLAnchorElement>) => {
        onClick?.(event);
        backOnClick(event);
      }}
      {...rest}
    />
  );
}
